
const ChemicalsList = () => {
  const chemicalCategories = [
    {
      category: "Drilling Fluids & Additives",
      description: "Essential chemicals for efficient drilling operations",
      chemicals: [
        { name: "Bentonite Clay", description: "Viscosity control and wellbore stability", applications: "Drilling mud, filtration control" },
        { name: "Barite", description: "Weighting agent for drilling fluids", applications: "Density control, pressure management" },
        { name: "Xanthan Gum", description: "Rheology modifier and suspension agent", applications: "Viscosity enhancement, cutting transport" },
        { name: "Polyanionic Cellulose (PAC)", description: "Filtration control additive", applications: "Fluid loss control, shale stabilization" },
        { name: "Caustic Soda", description: "pH control and alkalinity adjustment", applications: "Drilling fluid treatment, emulsification" },
        { name: "Potassium Chloride", description: "Shale inhibitor and stabilizer", applications: "Clay swelling prevention, wellbore stability" }
      ]
    },
    {
      category: "Production Chemicals",
      description: "Optimize production efficiency and equipment protection",
      chemicals: [
        { name: "Corrosion Inhibitors", description: "Protect equipment from corrosive environments", applications: "Pipeline protection, downhole equipment" },
        { name: "Scale Inhibitors", description: "Prevent mineral scale formation", applications: "Production tubing, surface equipment" },
        { name: "Biocides", description: "Control microbial growth and souring", applications: "Water treatment, reservoir protection" },
        { name: "Demulsifiers", description: "Separate oil-water emulsions", applications: "Production separation, crude oil treatment" },
        { name: "Foam Control Agents", description: "Eliminate foam in production systems", applications: "Separator optimization, gas processing" },
        { name: "Hydrate Inhibitors", description: "Prevent gas hydrate formation", applications: "Subsea operations, cold climate production" }
      ]
    },
    {
      category: "Enhanced Oil Recovery (EOR)",
      description: "Advanced chemicals for improved oil recovery",
      chemicals: [
        { name: "Surfactants", description: "Reduce interfacial tension for better sweep", applications: "Chemical flooding, mobility improvement" },
        { name: "Polymers", description: "Mobility control and viscosity enhancement", applications: "Polymer flooding, conformance control" },
        { name: "Alkali Chemicals", description: "pH modification for EOR processes", applications: "Alkaline flooding, surfactant optimization" },
        { name: "Foam Agents", description: "Gas mobility control in reservoirs", applications: "Foam flooding, gas channeling prevention" },
        { name: "Gel Systems", description: "Conformance control and water shutoff", applications: "Profile modification, water control" }
      ]
    },
    {
      category: "Well Stimulation",
      description: "Chemicals for fracturing and acidizing operations",
      chemicals: [
        { name: "Hydrochloric Acid", description: "Matrix acidizing and wellbore cleanup", applications: "Carbonate stimulation, scale removal" },
        { name: "Hydrofluoric Acid", description: "Sandstone matrix stimulation", applications: "Clay dissolution, permeability enhancement" },
        { name: "Gelling Agents", description: "Fracturing fluid viscosification", applications: "Proppant transport, fracture creation" },
        { name: "Crosslinkers", description: "High-temperature gel stability", applications: "Deep well fracturing, thermal stability" },
        { name: "Breakers", description: "Controlled gel degradation", applications: "Flowback enhancement, formation cleanup" },
        { name: "Friction Reducers", description: "Pumping efficiency improvement", applications: "Slickwater fracturing, pressure reduction" }
      ]
    },
    {
      category: "Water Treatment",
      description: "Comprehensive water management solutions",
      chemicals: [
        { name: "Coagulants", description: "Suspended solids removal", applications: "Produced water treatment, clarification" },
        { name: "Flocculants", description: "Enhanced settling and separation", applications: "Sludge dewatering, water clarification" },
        { name: "Oxygen Scavengers", description: "Dissolved oxygen removal", applications: "Injection water treatment, corrosion prevention" },
        { name: "pH Adjusters", description: "Water chemistry optimization", applications: "Treatment process control, equipment protection" },
        { name: "Antiscalants", description: "Scale prevention in water systems", applications: "Injection systems, heat exchangers" }
      ]
    },
    {
      category: "Pipeline & Transportation",
      description: "Chemicals for safe and efficient transportation",
      chemicals: [
        { name: "Drag Reducing Agents", description: "Pipeline flow improvement", applications: "Crude oil transportation, pressure drop reduction" },
        { name: "Pour Point Depressants", description: "Low temperature flow assurance", applications: "Waxy crude handling, cold climate operations" },
        { name: "Wax Inhibitors", description: "Paraffin deposition prevention", applications: "Pipeline maintenance, flow assurance" },
        { name: "Asphaltene Inhibitors", description: "Heavy fraction stabilization", applications: "Production optimization, equipment protection" },
        { name: "Thermal Stability Improvers", description: "High temperature operation support", applications: "Refinery processes, thermal cracking units" }
      ]
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Our Chemical Solutions Portfolio
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Golden Black supplies a comprehensive range of high-quality chemicals specifically formulated for oilfield operations. 
            Our products meet international standards and are customized to your specific operational requirements.
          </p>
        </div>

        <div className="grid gap-8">
          {chemicalCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-white rounded-lg shadow-lg p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{category.category}</h3>
                <p className="text-gray-600">{category.description}</p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.chemicals.map((chemical, chemicalIndex) => (
                  <div key={chemicalIndex} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <h4 className="text-lg font-semibold text-teal-600 mb-2">{chemical.name}</h4>
                    <p className="text-gray-700 mb-3 text-sm">{chemical.description}</p>
                    <div className="text-xs text-gray-500">
                      <strong>Applications:</strong> {chemical.applications}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="bg-teal-50 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Custom Formulations Available</h3>
            <p className="text-gray-700 mb-6 max-w-3xl mx-auto">
              Our expert engineers can design and produce custom chemical solutions tailored to your specific operational challenges. 
              We work closely with clients to develop formulations that meet unique requirements across various applications.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact" 
                className="bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Custom Solution
              </a>
              <button className="border-2 border-teal-600 text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-teal-600 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                Download Product Catalog
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChemicalsList;
